// A component that renders a list of tasks.

import { useCallback, useState, useRef } from 'react';
import { addBucketNameToTasks, graphQLSoon, soonThresholds, nameOverdue, nameSoon } from '../../fleet-shared/TaskBuckets.mjs';
import { bucketNameAll, bucketNameSoon, bucketNameOverdue } from '../../fleet-shared/Urls.mjs';
import { assignedFilter, assignedRange } from '../../fleet-shared/TaskAssigned.mjs';
import { client } from '../../utilities/Database.mjs';
import { SortOverdue, SortAssigned, SortPriority } from './Tasks.jsx';

// These are the fields I want back for a task.
const taskFields = `
  id
  name
  assignedEmail
  priority
  rank
  cost
  itemId
  description
  notes
  dueCounter
  dueDate
  counterInterval
  daysInterval
  monthsInterval
  itemCounter {
    counter
    name
  }
`;

// This is the query for the parent item, which includes the counters and their tasks.
// `soonDays` / `soonCounter` are the item's due-soon window; every date
// predicate and counter test below is built from the loaded item, never from
// the module defaults.
const getTaskPogoItem = /* GraphQL */ `
  getTaskPogoItem(id: $itemId) {
    id
    name
    parentId
    soonDays
    soonCounter
    itemCounter {
      items {
        id
        name
        counter
        tasks {
          items {
            ${taskFields}
          }
          nextToken
        }
      }
      nextToken
    }
  }
`;

// NOTE: prefer the GSI sort-key condition (`dueDatePriorityAssignedEmail`)
// over `filter` for date-range queries. AppSync applies `filter` AFTER
// paginating from DynamoDB, so a `{lt: now}` filter against an item with
// many future-dated tasks returns page-after-page of zero matches plus a
// nextToken, which causes the infinite-scroll loader to refetch forever
// without showing anything. The sort-key condition pushes the predicate
// down into DynamoDB so we only get matching rows back.
const tasksByItemIdAndDueDate = /* GraphQL */ `
  tasksByItemIdAndDueDate(
    itemId: $itemId
    dueDatePriorityAssignedEmail: $dueDatePriorityAssignedEmail
    sortDirection: $sortDirection
    filter: $filter
    nextToken: $nextToken
  ) {
    items {
      ${taskFields}
    }
    nextToken
  }
`;


// This query is for getting more counters.
export const itemCounterByItemIdQuery = /* GraphQL */ `
  query ItemCounterByItemId(
    $itemId: ID!
    $nextToken: String
  ) {
    itemCounterByItemId(
      itemId: $itemId
      nextToken: $nextToken
    ) {
      items {
        id
        name
        counter
      }
      nextToken
    }
  }
`;


// Counter predicates for the soon / future passes, built per item window.
//
// Soon: not yet due, and due within the window. Mirrors TaskBuckets'
// `shouldAdd` (0 < remaining <= window) and is the exact complement of the
// future test. It used to read `now <= due + window`, which is always true
// once `now < due`, so the soon pass admitted every future counter task and
// left restrictItemsToTaskListUrl to clean up.
function counterIsSoonWithin(window) {
  return (counter, taskCounter) => {
    if (taskCounter == null || !Number.isFinite(Number(taskCounter))) {
      return false;
    }
    const due = Number(taskCounter);
    const now = Number(counter);
    return now < due && due - now <= window;
  };
}

// Future: past the soon window.
function counterIsFutureBeyond(window) {
  return (counter, taskCounter) => {
    if (taskCounter == null || !Number.isFinite(Number(taskCounter))) {
      return false;
    }
    return Number(counter) < Number(taskCounter) - window;
  };
}

const LoadingOverdueByDate = 0;
const LoadingOverdueByCounters = 1;
const LoadingSoonByDate = 2;
const LoadingSoonByCounters = 3;
const LoadingFutureByDate = 4;
const LoadingNullDates = 5;
const LoadingAllCounters = 6;

// `/tasks/overdue` and `/tasks/soon` should only list tasks whose `bucketName`
// matches TaskBuckets (after `addBucketNameToTasks`). Catches GSI vs. bucket
// drift and bad counter predicates.
function restrictItemsToTaskListUrl(listType, items) {
  if (listType === bucketNameOverdue) {
    return items.filter((t) => t.bucketName === nameOverdue);
  }
  if (listType === bucketNameSoon) {
    return items.filter((t) => t.bucketName === nameSoon);
  }
  return items;
}

// AppSync sometimes omits empty @hasMany connections as null. Counter passes
// assume `item.itemCounter.items` exists; normalize so we never throw on null.
function ensureItemCounterConnection(item) {
  if (!item) {
    return;
  }
  if (!item.itemCounter) {
    item.itemCounter = { items: [], nextToken: null };
    return;
  }
  if (!item.itemCounter.items) {
    item.itemCounter.items = [];
  }
}

// This is a hook that fetches tasks that are overdue. It also fetches the item.
// I pass in setItem so that the item can be set in the parent component (and we
// don't re-call the query to get the tasks cuz the item changed).
export const useTaskQuery = (itemId, listType, sortType, setItem) => {
  const itemRef = useRef(null);
  const oldSort = useRef(null);
  const oldListType = useRef(null);
  // `useMyInfiniteScroll` can fire overlapping `fetchItems(null)` when
  // `fetchItems` is recreated (deps: itemId / listType / sort). A second run
  // used to reset `itemRef` while the first was mid-await → null.itemCounter.
  const fetchSortOverdueChainTailRef = useRef(Promise.resolve());

  // All items with a dueDate < now are overdue. The "soon" window is the
  // item's own, so it is read off `itemRef.current` at the point of use
  // (`graphQLSoon(itemRef.current)`) rather than fixed at mount.
  const [now] = useState(new Date());

  const loadingState = useRef(LoadingOverdueByDate);
  const scratchpad = useRef(null);


  // This takes the item and if it has more counters to fetch, fetches them.
  const fetchAllItemCounters = useCallback(async (item) => {
    ensureItemCounterConnection(item);
    let counters = item.itemCounter.items;
    let nextToken = item.itemCounter.nextToken;

    while (nextToken) {
      console.log(`Fetching more counters for itemId ${itemId}: ${nextToken}`);
      const itemsData = await client.graphql({ query: itemCounterByItemIdQuery, variables: { itemId: itemId, nextToken: nextToken }});
      const newCounters = itemsData.data.itemCounterByItemId.items;
      counters = counters.concat(newCounters);
      nextToken = itemsData.data.itemCounterByItemId.nextToken;
    }

    item.itemCounter.items = counters;
  }, [itemId]);


  // This is the fetch for more tasks by date.
  //
  // `condition` controls how we constrain the query:
  //   - { sortKey: <ModelTaskPogoTaskTaskPogoTasksByItemIdAndDueDateAndPriorityAndAssignedEmailCompositeKeyConditionInput> }
  //     is pushed into the GSI as a sort-key range so DynamoDB only returns
  //     rows whose dueDate matches. Use this for date-range queries
  //     (overdue, soon) — otherwise AppSync paginates through every task
  //     and the filter throws most pages away, which used to cause the
  //     infinite "refetching N" loop on items with many future-dated tasks.
  //   - { filter: <ModelTaskPogoTaskFilterInput> } falls back to the
  //     post-fetch filter (used for the "null dueDate" sweep, since null
  //     sort keys aren't representable as a sort-key range).
  //   - omit `condition` entirely to fetch every row in the GSI.
  const fetchByDate = useCallback(async (token, condition) => {
    var query = `
      query TasksByItemIdAndDueDate(
        $itemId: ID!
        $sortDirection: ModelSortDirection
        $dueDatePriorityAssignedEmail: ModelTaskPogoTaskTaskPogoTasksByItemIdAndDueDateAndPriorityAndAssignedEmailCompositeKeyConditionInput
        $filter: ModelTaskPogoTaskFilterInput
        $nextToken: String
      ) {
        ${itemRef.current ? '' : getTaskPogoItem}
        ${tasksByItemIdAndDueDate}
      }
    `;
    var variables = {
      itemId: itemId,
      sortDirection: 'ASC'
    };

    if (condition && condition.sortKey) {
      variables.dueDatePriorityAssignedEmail = condition.sortKey;
    }
    if (condition && condition.filter) {
      variables.filter = condition.filter;
    }

    if (token) {
      variables.nextToken = token;
    }

    const itemsData = await client.graphql({ query: query, variables: variables });

    // Remember the item.
    if (itemsData.data.getTaskPogoItem) {
      const loaded = itemsData.data.getTaskPogoItem;
      ensureItemCounterConnection(loaded);
      itemRef.current = loaded;
      setItem(itemRef.current);
    }

    // First batch of overdue tasks based on date:
    var result = itemsData.data["tasksByItemIdAndDueDate"];
    var items = result.items;
    var nextToken = result.nextToken;
    addBucketNameToTasks(items, itemRef.current);

    return [ items, nextToken, itemsData.data.getTaskPogoItem ];  

  }, [itemId, setItem]);


  // Loads just the item (the same fragment fetchByDate folds into its first
  // query), for a phase that starts with no item loaded. `/tasks/soon` under
  // the default sort begins at LoadingSoonByDate, whose GSI range is the
  // item's due-soon window -- so the item has to be read BEFORE that query
  // can be built. Once `itemRef.current` is set, fetchByDate omits the
  // fragment on its own, so nothing is read twice.
  const fetchItemOnly = useCallback(async () => {
    const query = `
      query GetTaskPogoItemForTaskList($itemId: ID!) {
        ${getTaskPogoItem}
      }
    `;
    const itemsData = await client.graphql({ query, variables: { itemId } });
    const loaded = itemsData.data.getTaskPogoItem;
    if (loaded) {
      ensureItemCounterConnection(loaded);
      itemRef.current = loaded;
      setItem(loaded);
    }
    return loaded;
  }, [itemId, setItem]);


  // This is the fetch searching for overdue items by counter.
  const fetchUsingCounters = useCallback(async (token, includeFunction) => {
    let items = [];
    let nextToken = null;

    // If there's a nextToken, we've been called to get more tasks.
    if (token) {
      const itemsData = await client.graphql({ query: itemCounterByItemIdQuery, variables: { itemId: itemId, nextToken: token }});

      if (!itemRef.current) {
        console.error("No itemRef.current in fetchUsingCounters");
        return [[], null];
      }
      ensureItemCounterConnection(itemRef.current);

      const newCounters = itemsData.data.itemCounterByItemId.items;

      // Add the new counters to the itemRef.current.itemCounter.items.
      for (let newCounter of newCounters) {
        itemRef.current.itemCounter.items.push(newCounter);
      }
    }

    if (!itemRef.current?.itemCounter?.items) {
      console.error(
        'fetchUsingCounters: missing item or itemCounter (concurrent fetch reset?)'
      );
      return [[], null];
    }
    ensureItemCounterConnection(itemRef.current);

    // Start looking through the counters and find matching tasks.
    for (let itemCounter of itemRef.current.itemCounter.items) {
      // If we've already processed this counter, skip it.
      if (itemCounter.processed) { continue }

      if (!itemCounter.tasks?.items) {
        if (!itemCounter.tasks) {
          itemCounter.tasks = { items: [], nextToken: null };
        } else {
          itemCounter.tasks.items = [];
        }
      }

      // Inspect every task on this counter. A non-match must not skip the
      // rest of the list (the old `continue counterLoop` stopped scanning
      // after the first miss, hiding real overdue/soon rows behind e.g.
      // `dueCounter: null` siblings).
      let tasks = itemCounter.tasks.items;
      for (let task of tasks) {
        // If we've already processed this task, skip it.
        if (task.processed) { continue }

        if (includeFunction(itemCounter.counter, task.dueCounter)) {
          items.push(task);
          task.processed = true;
        }
      }
      // All tasks processed, so mark this counter as processed.
      itemCounter.processed = true;


      // If there's a nextToken, return it, we'll get called back with it for more.
      nextToken = itemCounter.tasks.nextToken;
      if (nextToken) { 
        // Don't want to return this nextToken again -- we'll use it in the next call and not again.
        itemCounter.tasks.nextToken = null;
        break;
      }
    }

    addBucketNameToTasks(items, itemRef.current);
    return [items, nextToken];
  }, [itemId]);


  // Test if the counter is overdue (meter at or past dueCounter).
  // `dueCounter == null` must be false: in JS `counter >= null` coerces null→0,
  // so every non-negative reading would look "overdue" while `addBucketNameToTasks`
  // still labels those tasks "All" → "Not Due" on the overdue page.
  function counterIsOverdue(counter, taskCounter) {
    if (taskCounter == null || !Number.isFinite(Number(taskCounter))) {
      return false;
    }
    return Number(counter) >= Number(taskCounter);
  }

  // The item's due-soon counter window, read off the loaded item at the
  // point of use (`soonThresholds(itemRef.current).counter`) and handed to
  // the module-level predicate factories below -- a predicate closing over
  // the ref from hook scope would be a dependency the state machine's
  // useCallback has to name.

  // This calls the passed in fetch function and adds the items to the items array, if the
  // array doesn't already contain the item.
  async function doTheFetch(theFetch, arg1, arg2, items) {
    var [i, n] = await theFetch(arg1, arg2);
    if (!Array.isArray(i)) {
      i = [];
    }

    // Add the items to the list, if they're not already there.
    for (let item of i) {
      if (!items.find((element) => element.id === item.id)) {
        items.push(item);
      }
    }
    return [items, n];
  }


  // This is the fetch function when sorting by overdue. 
  // TODO: Make not a state machine. Do everything in parallel.
  // It's a state machine that fetches tasks by date and then by counter.
  const fetchSortOverdueBody = useCallback(async (token) => {
    console.log(`*** FetchItems ${token ? '' : 'No token'} ***`);
    var items = [];
    let nextToken = null;
    let soonWindow = null;

    // The three counter phases each walk itemRef.current's counters, and
    // fetchUsingCounters flags a counter `processed` once a phase has walked
    // it so a token continuation WITHIN the phase doesn't rescan it. That
    // flag has to be cleared when the next counter phase begins, or that
    // phase walks nothing: left set, `/tasks/all` under this sort never
    // listed a counter task that wasn't already overdue. (A task's own
    // `processed` stays -- a task admitted by one phase is not a candidate
    // for the next.)
    const beginCounterPhase = (state) => {
      loadingState.current = state;
      for (const counter of itemRef.current?.itemCounter?.items ?? []) {
        counter.processed = false;
      }
    };

    // If no token, we might be starting over. Start the state machine over.
    // If the sort or URL bucket changed, reset too — `fetchSortOverdue` is
    // recreated when `listType` changes (dependency array), but pagination
    // calls pass a non-null token; without resetting here, `loadingState` /
    // `itemRef` stay on the old pipeline while `listType` drives filters
    // and early returns (wrong rows or wasted work). A cursor from one GSI
    // phase must not continue after a bucket/sort change either.
    const sortChanged = oldSort.current !== sortType;
    const listTypeChanged = oldListType.current !== listType;
    if (token === null || sortChanged || listTypeChanged) {
      if (token != null && (sortChanged || listTypeChanged)) {
        token = null;
      }
      // URL bucket `soon` / `overdue` must not run the full pipeline: the
      // default "Overdue First" sort uses this state machine to build one
      // merged list (overdue → soon → future). Filtering that list breaks
      // pagination (early pages are only overdue rows). Start / stop in
      // the right phase for the bucket instead.
      loadingState.current =
        listType === bucketNameSoon ? LoadingSoonByDate : LoadingOverdueByDate;
      oldSort.current = sortType;
      oldListType.current = listType;
      itemRef.current = null;
    }

    // Loops until gets a nextToken or done.
    for (;;) {
      switch (loadingState.current) {
        case LoadingOverdueByDate:
          // In this state, I look for tasks that are overdue by date.
          // Use a sort-key range (`lt: { dueDate: now }`) so DynamoDB
          // only returns overdue rows; a post-fetch filter would page
          // through every future-dated task forever.
          console.log(`LoadingByDate: ${token ? 'with token' : 'null'}`);
          var [i, n] = await doTheFetch(
            fetchByDate,
            token,
            { sortKey: { lt: { dueDate: now.toISOString() } } },
            items
          );
          items = i;
          nextToken = n;

          // No nextToken, move on to the next state.
          if (!nextToken) {
            beginCounterPhase(LoadingOverdueByCounters);
          }
          break;

        case LoadingOverdueByCounters:
          // In this state, I look through the item's counters and find tasks that are overdue.
          console.log(`LoadingOverdueByCounters: ${token ? 'with token' : 'null'}`);

          // TODO: What if I already added the task in LoadingFirst or LoadingByDate?
          [i, n] = await doTheFetch(fetchUsingCounters, token, counterIsOverdue, items);
          items = i;
          nextToken = n;

          // If there is no nextToken, move on to the next state (or finish
          // for the `/tasks/overdue` URL — that view must not load soon/future
          // tasks).
          if (!nextToken) {
            if (listType === bucketNameOverdue) {
              return {
                items: restrictItemsToTaskListUrl(listType, items),
                nextToken: null,
              };
            }
            loadingState.current = LoadingSoonByDate;
          }
          break;

        case LoadingSoonByDate:
          // Now load all the tasks that are soon. Same reasoning as
          // LoadingOverdueByDate — push the date range into the GSI sort
          // key so we don't paginate through unrelated rows.
          console.log(`LoadingSoonByDate: ${token ? 'with token' : 'null'}`);

          // The range is the item's window, so the item comes first when
          // this is the phase we started in (`/tasks/soon`).
          if (!itemRef.current) {
            await fetchItemOnly();
          }
          soonWindow = graphQLSoon(itemRef.current);

          [i, n] = await doTheFetch(
            fetchByDate,
            token,
            {
              sortKey: {
                between: [
                  { dueDate: soonWindow.between[0] },
                  { dueDate: soonWindow.between[1] },
                ],
              },
            },
            items
          );
          items = i;
          nextToken = n;

          // If no next token, move on to the next state.
          if (!nextToken) {
            beginCounterPhase(LoadingSoonByCounters);
          }
          break;

        case LoadingSoonByCounters:
          console.log(`LoadingSoonByCounters: ${token ? 'with token' : 'null'}`);

          // Now load all the tasks that are soon.
          [i, n] = await doTheFetch(
            fetchUsingCounters,
            token,
            counterIsSoonWithin(soonThresholds(itemRef.current).counter),
            items
          );
          items = i;
          nextToken = n;

          // If no next token, move on (or finish for `/tasks/soon` — no
          // future-dated / undated sweep for that bucket).
          if (!nextToken) {
            if (listType === bucketNameSoon) {
              return {
                items: restrictItemsToTaskListUrl(listType, items),
                nextToken: null,
              };
            }
            // Calendar-dated tasks after the "soon" window (e.g. next year)
            // never matched LoadingSoonByDate's `between` and are not
            // counter-driven — without this phase `/tasks/all` omitted them.
            loadingState.current =
              listType === bucketNameAll
                ? LoadingFutureByDate
                : LoadingNullDates;
          }
          break;

        case LoadingFutureByDate:
          console.log(`LoadingFutureByDate: ${token ? 'with token' : 'null'}`);
          [i, n] = await doTheFetch(
            fetchByDate,
            token,
            {
              sortKey: {
                gt: { dueDate: graphQLSoon(itemRef.current).between[1] },
              },
            },
            items
          );
          items = i;
          nextToken = n;

          if (!nextToken) {
            loadingState.current = LoadingNullDates;
          }
          break;

        case LoadingNullDates:
          console.log(`LoadingNullDates: ${token ? 'with token' : 'null'}`);

          // Tasks with a null `dueDate` aren't in the byItemIdAndDueDate
          // GSI at all (DynamoDB drops items missing the sort key), so
          // querying that index for them is a no-op. Counter-only and
          // dateless tasks are picked up by LoadingAllCounters below.
          // Skipping straight to the next state avoids a pointless full
          // GSI scan that would page through every dated task returning
          // zero matches.
          [i, n] = [items, null];
          items = i;
          nextToken = n;

          // If no next token, we're done.
          if (null == nextToken) {
            beginCounterPhase(LoadingAllCounters);
          }
          break;

        case LoadingAllCounters:
          console.log(`LoadingAllCounters: ${token ? 'with token' : 'null'}`);
          [i, n] = await doTheFetch(
            fetchUsingCounters,
            token,
            counterIsFutureBeyond(soonThresholds(itemRef.current).counter),
            items
          );
          items = i;
          nextToken = n;

          // If no next token, done.
          if (!nextToken) {
            return {
              items: restrictItemsToTaskListUrl(listType, items),
              nextToken,
            };
          }
          break;
          
        default:
          console.error("Unknown state.");
          throw new Error("Unknown state.");
      }

      // I've used the passed in token. Clear it, so don't use it in next state if looping.
      token = null;

      // If there's a nextToken, return -- we'll get called back for when the user scrolls to the bottom of the list.
      // If there isn't a nextToken, we should have moved to the next state in the state machine.
      if (nextToken) {
        return {
          items: restrictItemsToTaskListUrl(listType, items),
          nextToken,
        };
      }
    }
  }, [fetchByDate, fetchItemOnly, fetchUsingCounters, now, sortType, listType]);

  const fetchSortOverdue = useCallback(
    async (token) => {
      const prevChain = fetchSortOverdueChainTailRef.current;
      let releaseNext;
      fetchSortOverdueChainTailRef.current = new Promise((resolve) => {
        releaseNext = resolve;
      });
      await prevChain;
      try {
        return await fetchSortOverdueBody(token);
      } finally {
        releaseNext();
      }
    },
    [fetchSortOverdueBody]
  );


  // This is the query for tasks assigned to someone. Call with the name
  // used in the query as the filter for this query.
  const tasksByItemIdAndAssigned =  useCallback((filterName) => {
    return `tasksByItemIdAndAssigned(
        itemId: $itemId
        assignedEmailPriority: $assignedEmailPriority
        sortDirection: $sortDirection
        filter: $${filterName}
        limit: $limit
        nextToken: $nextToken
      ) {
        items {
          ${taskFields}
        }
        nextToken
      }
    `;
  }, []);


  // This is the fetch for tasks sorted by assigned.
  const fetchSortAssigned = useCallback(async (token) => {   
    // If there is no token, this is the initial query. Setup the nextToken state machine.
    const stateInitial = 0;
    const stateAssigned = 1;
    const stateUnassigned = 2;
    const stateDone = 3;

    if (!token) {
      loadingState.current = stateInitial;
      itemRef.current = null;
    }

    var variables = {
      itemId: itemId,
      sortDirection: 'ASC',
      // null on the initial call (page 1 of both sub-lists); on later calls
      // this is the cursor for whichever single sub-list the current state
      // re-queries.
      nextToken: token,
      assignedFilter: {
        and: [
          { assignedEmail: {ne: null, attributeExists: true} },
          { assignedEmail: {ne: '', attributeExists: true} }
        ]
      },
      unassignedFilter: {
        or: [ 
          { assignedEmail: {eq: null, attributeExists: true} },
          { assignedEmail: {eq: '', attributeExists: true} }
        ]
      }
    };


    let query = null;
    var itemsData = null;
    var items = [];
    var nextToken = null;

    switch (loadingState.current) {
      case stateInitial:
        query = `query TasksByItemIdAndAssigned(
          $itemId: ID!
          $assignedEmailPriority: ModelTaskPogoTaskTaskPogoTasksByItemIdAndAssignedEmailAndPriorityCompositeKeyConditionInput
          $sortDirection: ModelSortDirection
          $assignedFilter: ModelTaskPogoTaskFilterInput
          $unassignedFilter: ModelTaskPogoTaskFilterInput
          $limit: Int
          $nextToken: String
        ) {
          ${getTaskPogoItem}
          assignedTasks: ${tasksByItemIdAndAssigned('assignedFilter')}
          unassignedTasks: ${tasksByItemIdAndAssigned('unassignedFilter')}
        }`;
        itemsData = await client.graphql({ query: query, variables: variables });
        
        // Go get any counters not already fetched in the item and remember the item.
        // TODO: This could be a bunch of roundtrips, do I need it? I don't anticipate
        // lots of counters, so I'll assume it's ok for now.
        const loadedItem = itemsData.data.getTaskPogoItem;
        if (loadedItem) {
          ensureItemCounterConnection(loadedItem);
          await fetchAllItemCounters(loadedItem);
          itemRef.current = loadedItem;
          setItem(loadedItem);
        }

        // Get the items out of the assigned tasks.
        items = itemsData.data.assignedTasks.items;
        nextToken = itemsData.data.assignedTasks.nextToken;

        // If there is a nextToken, I will return the assigned tasks and the nextToken.
        // And we'll get called back with the nextToken later. Handle that in the next state.
        if (nextToken) {
          // More assigned pages to come. Stash the unassigned first page (with
          // its own cursor) to resume once assigned is exhausted -- stash the
          // connection itself so `.items`/`.nextToken` are present in
          // stateAssigned (not the whole response object).
          loadingState.current = stateAssigned;
          scratchpad.current = itemsData.data.unassignedTasks;
        } else {
          // Concatenate assigned and unassigned tasks.
          items = items.concat(itemsData.data.unassignedTasks.items);

          // If there is a nextToken in unassignedTasks, I will return it and we'll
          // get called back with it later. Handle that in the next state.
          if (itemsData.data.unassignedTasks.nextToken) {
            loadingState.current = stateUnassigned;
            scratchpad.current = itemsData.data.unassignedTasks;
            nextToken = itemsData.data.unassignedTasks.nextToken;
          } else {
            loadingState.current = stateDone;
          }
        }
        break;
      case stateAssigned:
        // In this state, we are passed the nextToken for the assigned tasks query.
        query = `query TasksByItemIdAndAssigned(
          $itemId: ID!
          $assignedEmailPriority: ModelTaskPogoTaskTaskPogoTasksByItemIdAndAssignedEmailAndPriorityCompositeKeyConditionInput
          $sortDirection: ModelSortDirection
          $assignedFilter: ModelTaskPogoTaskFilterInput
          $limit: Int
          $nextToken: String
        ) {
          assignedTasks: ${tasksByItemIdAndAssigned('assignedFilter')}
        }`;
        itemsData = await client.graphql({ query: query, variables: variables });
        items = itemsData.data.assignedTasks.items;
        nextToken = itemsData.data.assignedTasks.nextToken;

        // If there is a nextToken, I will return the assigned tasks and the nextToken.
        // If not, I will concatenate the unassigned tasks and return them.
        if (!nextToken) {
          // Concatenate assigned and unassigned tasks.
          items = items.concat(scratchpad.current.items);

          // If there is a nextToken in unassignedTasks, I will return it and we'll
          // get called back with it later. Handle that in the next state.
          if (scratchpad.current.nextToken) {
            loadingState.current = stateUnassigned;
            nextToken = scratchpad.current.nextToken;
          } else {
            loadingState.current = stateDone;
          }
        }
        break;

      case stateUnassigned:
        // In this state, we are passed the nextToken for the unassigned tasks query.
        query = `query TasksByItemIdAndAssigned(
          $itemId: ID!
          $assignedEmailPriority: ModelTaskPogoTaskTaskPogoTasksByItemIdAndAssignedEmailAndPriorityCompositeKeyConditionInput
          $sortDirection: ModelSortDirection
          $unassignedFilter: ModelTaskPogoTaskFilterInput
          $limit: Int
          $nextToken: String
        ) {
          unassignedTasks: ${tasksByItemIdAndAssigned('unassignedFilter')}
        }`;
        itemsData = await client.graphql({ query: query, variables: variables });
        items = itemsData.data.unassignedTasks.items;
        nextToken = itemsData.data.unassignedTasks.nextToken;

        // If there is a nextToken, I will return the assigned tasks and the nextToken.
        // If not, I'm done.
        if (!nextToken) {
          loadingState.current = stateDone;
        }
        break;

      case stateDone:
        throw new Error("Shouldn't be called back in stateDone.");

      default:
        console.error(`Unknown state in fetchSortAssigned: ${loadingState.current}`);
        throw new Error("Unknown state.");
    }
    addBucketNameToTasks(items, itemRef.current);
    items = filterForListType(listType, items);
    return { items, nextToken };
  }, [itemId, setItem, tasksByItemIdAndAssigned, fetchAllItemCounters, listType]);


  const fetchSortPriority = useCallback(async (token) => {
    // Two sub-lists -- priority tasks first, then no-priority tasks -- each
    // paginated independently. They can't share a single $nextToken (a priority
    // cursor is meaningless to the no-priority query, and the previous code fed
    // one token to both connections and discarded the other list's first page),
    // so sequence them with the same loadingState state machine
    // fetchSortAssigned uses: drain priority fully, then drain no-priority.
    const stateInitial = 0;
    const statePriority = 1;
    const stateNoPriority = 2;
    const stateDone = 3;

    if (!token) {
      loadingState.current = stateInitial;
      itemRef.current = null;
    }

    var variables = {
      itemId: itemId,
      sortDirection: 'ASC',
      // null on the initial call (page 1 of both sub-lists); on later calls
      // this is the cursor for whichever single sub-list the current state
      // re-queries.
      nextToken: token,
      priorityFilter: {
        and: [
          {
            priority: {ne: 0, attributeExists: true}
          }
        ]
      },
      noPriorityFilter: {
        or: [
          {
            priority: {eq: 0, attributeExists: true}
          },
          {
            priority: {attributeExists: false}
          }
        ]
      }
    };

    // One paginated `tasksByItemIdAndPriority` connection, filtered by the given
    // variable name ('priorityFilter' or 'noPriorityFilter').
    const priorityConnection = (filterVar) => `tasksByItemIdAndPriority(
        itemId: $itemId
        priorityAssignedEmail: $priorityAssignedEmail
        sortDirection: $sortDirection
        filter: $${filterVar}
        limit: $limit
        nextToken: $nextToken
      ) {
        items {
          ${taskFields}
        }
        nextToken
      }`;

    let query = null;
    var itemsData = null;
    var items = [];
    var nextToken = null;

    switch (loadingState.current) {
      case stateInitial:
        // Fetch the item plus the first page of BOTH sub-lists.
        query = `query TasksByItemIdAndPriority(
          $itemId: ID!
          $priorityAssignedEmail: ModelTaskPogoTaskTaskPogoTasksByItemIdAndPriorityAndAssignedEmailCompositeKeyConditionInput
          $sortDirection: ModelSortDirection
          $priorityFilter: ModelTaskPogoTaskFilterInput
          $noPriorityFilter: ModelTaskPogoTaskFilterInput
          $limit: Int
          $nextToken: String
        ) {
          ${getTaskPogoItem}
          priorityTasks: ${priorityConnection('priorityFilter')}
          noPriorityTasks: ${priorityConnection('noPriorityFilter')}
        }`;
        itemsData = await client.graphql({ query: query, variables: variables });

        // Remember the item. Fully resolve its counters up front (same as
        // fetchSortAssigned) so the edit-task popup's counter dropdown is
        // complete regardless of which sort loaded the page.
        const loaded = itemsData.data.getTaskPogoItem;
        if (loaded) {
          ensureItemCounterConnection(loaded);
          await fetchAllItemCounters(loaded);
          itemRef.current = loaded;
          setItem(loaded);
        }

        items = itemsData.data.priorityTasks.items;
        nextToken = itemsData.data.priorityTasks.nextToken;

        if (nextToken) {
          // More priority pages to come. Stash the no-priority first page (with
          // its own cursor) to resume once priority is exhausted.
          loadingState.current = statePriority;
          scratchpad.current = itemsData.data.noPriorityTasks;
        } else {
          // Priority fit in one page; append the no-priority first page.
          items = items.concat(itemsData.data.noPriorityTasks.items);
          if (itemsData.data.noPriorityTasks.nextToken) {
            loadingState.current = stateNoPriority;
            nextToken = itemsData.data.noPriorityTasks.nextToken;
          } else {
            loadingState.current = stateDone;
          }
        }
        break;

      case statePriority:
        // `token` is a priorityTasks cursor.
        query = `query TasksByItemIdAndPriority(
          $itemId: ID!
          $priorityAssignedEmail: ModelTaskPogoTaskTaskPogoTasksByItemIdAndPriorityAndAssignedEmailCompositeKeyConditionInput
          $sortDirection: ModelSortDirection
          $priorityFilter: ModelTaskPogoTaskFilterInput
          $limit: Int
          $nextToken: String
        ) {
          priorityTasks: ${priorityConnection('priorityFilter')}
        }`;
        itemsData = await client.graphql({ query: query, variables: variables });
        items = itemsData.data.priorityTasks.items;
        nextToken = itemsData.data.priorityTasks.nextToken;

        if (!nextToken) {
          // Priority exhausted -- append the stashed no-priority first page and
          // switch to draining it.
          items = items.concat(scratchpad.current.items);
          if (scratchpad.current.nextToken) {
            loadingState.current = stateNoPriority;
            nextToken = scratchpad.current.nextToken;
          } else {
            loadingState.current = stateDone;
          }
        }
        break;

      case stateNoPriority:
        // `token` is a noPriorityTasks cursor.
        query = `query TasksByItemIdAndPriority(
          $itemId: ID!
          $priorityAssignedEmail: ModelTaskPogoTaskTaskPogoTasksByItemIdAndPriorityAndAssignedEmailCompositeKeyConditionInput
          $sortDirection: ModelSortDirection
          $noPriorityFilter: ModelTaskPogoTaskFilterInput
          $limit: Int
          $nextToken: String
        ) {
          noPriorityTasks: ${priorityConnection('noPriorityFilter')}
        }`;
        itemsData = await client.graphql({ query: query, variables: variables });
        items = itemsData.data.noPriorityTasks.items;
        nextToken = itemsData.data.noPriorityTasks.nextToken;

        if (!nextToken) {
          loadingState.current = stateDone;
        }
        break;

      case stateDone:
        throw new Error("Shouldn't be called back in stateDone.");

      default:
        console.error(`Unknown state in fetchSortPriority: ${loadingState.current}`);
        throw new Error("Unknown state.");
    }

    addBucketNameToTasks(items, itemRef.current);

    // Post-filter to drop tasks based on the list-type.
    items = filterForListType(listType, items);

    return { items, nextToken };
  }, [itemId, setItem, listType, fetchAllItemCounters]);


  const fetchItems = useCallback(async (token) => {
    switch (sortType) {
      case SortOverdue:
        return fetchSortOverdue(token);
      case SortAssigned:
        return fetchSortAssigned(token);
      case SortPriority:
        return fetchSortPriority(token);
      default:
        throw new Error(`Unknown sort type ${sortType}`);
    }
  }, [fetchSortAssigned, fetchSortOverdue, fetchSortPriority, sortType]);

  return  fetchItems;
}


// Every open task on the item assigned to exactly `assignee` -- the page behind
// the assigned-name links on the task rows. Returns a `fetchItems(token)` for
// TaskTable, like useTaskQuery, but there is one list and one cursor, so no
// state machine.
//
// The range is TaskAssigned.assignedRange (the GSI prefix WITH the composite
// separator, so "bob" does not collect "bobby"); the eq filter beside it is the
// belt-and-braces that module explains. Pushing the predicate into the sort key
// is what keeps this off the empty-page pagination loop the note above
// tasksByItemIdAndDueDate warns about: the filter here can only drop a
// '#'-in-a-name collision, never page after page.
//
// The item (with its soon window) is folded into the first call and remembered,
// exactly as fetchByDate does, so every page's buckets are computed against the
// item the page is about. `taskFields` already selects `itemCounter { counter
// name }`, so a counter task arrives ready to bucket.
export const useAssignedToTaskQuery = (itemId, assignee, setItem) => {
  const itemRef = useRef(null);

  return useCallback(async (token) => {
    if (!token) {
      itemRef.current = null;
    }

    const query = `
      query TasksByItemIdAssignedTo(
        $itemId: ID!
        $assignedEmailPriority: ModelTaskPogoTaskTaskPogoTasksByItemIdAndAssignedEmailAndPriorityCompositeKeyConditionInput
        $filter: ModelTaskPogoTaskFilterInput
        $sortDirection: ModelSortDirection
        $nextToken: String
      ) {
        ${itemRef.current ? '' : getTaskPogoItem}
        tasksByItemIdAndAssigned(
          itemId: $itemId
          assignedEmailPriority: $assignedEmailPriority
          filter: $filter
          sortDirection: $sortDirection
          nextToken: $nextToken
        ) {
          items {
            ${taskFields}
          }
          nextToken
        }
      }
    `;
    const variables = {
      itemId,
      sortDirection: 'ASC',
      assignedEmailPriority: assignedRange(assignee),
      filter: assignedFilter(assignee),
    };
    if (token) {
      variables.nextToken = token;
    }

    const { data } = await client.graphql({ query, variables });

    if (data.getTaskPogoItem) {
      const loaded = data.getTaskPogoItem;
      ensureItemCounterConnection(loaded);
      itemRef.current = loaded;
      setItem(loaded);
    }

    const page = data.tasksByItemIdAndAssigned;
    const items = page?.items ?? [];
    addBucketNameToTasks(items, itemRef.current);
    return { items, nextToken: page?.nextToken ?? null };
  }, [itemId, assignee, setItem]);
};


// Filter the items based on the list type. So, if the list type is overdue, only want the overdue tasks,
// filtering out the others.
function filterForListType(listType, items) {
  switch (listType) {
    case bucketNameAll:
      // Want everything, so no more filtering.
      break;
    case bucketNameOverdue:
      // Overdue tasks only. So only want dueDate <= now or counter <= taskCounter.
      items = items.filter((task) => { return task.bucketName === nameOverdue; });
      break;
    case bucketNameSoon:
      items = items.filter((task) => { return task.bucketName === nameSoon; });
      break;
    default:
      break;
  }
  return items;
}

